import { useQuery } from 'react-apollo';

import { DRAFT_MESSAGE_THREAD_ID_PREFIX } from 'Domains/Messages/constants';
import {
  MessagesMessageThreadMessagesErrorFragment,
  MessagesMessageThreadMessagesQuery,
  MessagesMessageThreadMessagesQueryVariables,
  MessagesMessageThreadMessagesSuccessFragment,
} from 'Src/generated/graphql';

import { GET_MESSAGE_THREAD_MESSAGES_QUERY } from '../queries/messageThreadMessages';

function isError(
  messageThreadMessages:
    | MessagesMessageThreadMessagesSuccessFragment
    | MessagesMessageThreadMessagesErrorFragment
    | undefined
    | null,
): messageThreadMessages is MessagesMessageThreadMessagesErrorFragment {
  return (
    Boolean(messageThreadMessages) &&
    (messageThreadMessages as MessagesMessageThreadMessagesErrorFragment)
      .__typename === 'GenericError'
  );
}

export default function useMessageThreadMessages(
  params: {
    threadId: string | null;
    pollInterval?: number;
  } = {
    threadId: null,
  },
) {
  const isSkipping =
    !params.threadId ||
    params.threadId.indexOf(DRAFT_MESSAGE_THREAD_ID_PREFIX) >= 0;

  const result = useQuery<
    MessagesMessageThreadMessagesQuery,
    MessagesMessageThreadMessagesQueryVariables
  >(GET_MESSAGE_THREAD_MESSAGES_QUERY, {
    variables: {
      threadId: params.threadId || '',
    },
    skip: isSkipping,
    fetchPolicy: 'cache-and-network',
    pollInterval: params.pollInterval,
  });

  const messageThreadMessages = result.data?.messageThreadMessages;

  if (result.error) {
    return {
      ...result,
      data: undefined,
      error: result.error,
    };
  }

  if (isError(messageThreadMessages)) {
    return {
      ...result,
      data: undefined,
      error: messageThreadMessages,
    };
  }

  return {
    ...result,
    data: {
      messageThreadMessages:
        messageThreadMessages ||
        ({
          nodes: [],
          pagination: { currentPage: 0, perPage: 0, total: 0, totalPages: 0 },
        } as MessagesMessageThreadMessagesSuccessFragment),
    },
    error: undefined,
  };
}
